import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Header,
  SpaceBetween,
  Table,
  TableProps,
  Container,
  AppLayout,
  ContentLayout,
  TextFilter,
  StatusIndicator,
} from '@cloudscape-design/components';
import PropertyFilter, {
  PropertyFilterProps,
} from '@cloudscape-design/components/property-filter';
import { useNavigate } from 'react-router-dom';
import routes from '../../contstant/routePaths';
import QuestionCategoryDetailsForm from '../../components/questionCategory/QuestionCategoryDetailsForm';
import { Category, ListCategoriesRequest } from '../../types/helios/Category';
import {
  getCategoryColumnDefinitions,
  getExportCategoryConfig,
  getItemValue,
  getSearchableFields,
  initialFilters,
  getFilterCategoriesProperties,
  createCategoryPreferences,
  getCategoryFilterOptions,
  contentDisplay,
} from './question-categories-config';
import { useCategories } from 'src/store/categories/useCategories';
import useUserProfile from 'src/hooks/useUserProfile';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import { resetAllApiStatus } from 'src/store/categories/categoriesSlice';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import { ExportButton } from 'src/components/ExportButton';
import FilterModal from 'src/components/filter/FilterModel';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { Pagination } from '@cloudscape-design/components';
import { Persona } from 'src/contstant/Persona';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import { useUsers } from 'src/store/users/useUsers';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { usePropertyFilterPersistence } from 'src/hooks/usePropertyFilterPersistence';
import { useActivePropertyFilterCount } from 'src/hooks/useActivePropertyFilterCount';
import { getPageFilterKey } from 'src/contstant/storageKeys';

const QuestionsCategoryPage = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category[]>([]);
  const [visibleColumns, setVisibleColumns] = useState(contentDisplay);
  const [tablePrefs, setTablePrefs] = useState({
    pageSize: 10,
  });

  const { setContent, setTitle, closeView } = useSplitPanel();

  const { getPersona, getUserProgramId, getUserId, getUserBusinessUnitId, getDivisions } =
    useUserProfile();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): any => {
    return {
      tokens: [],
      operation: 'and' as const,
      searchText: '',
    };
  }, []);

  // Use filter persistence hook
  const { filters: queryFilters, setFilters: setQueryFilters } = useFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'categories'),
    defaultFilters: getDefaultFilters(),
    excludeFields: ['businessUnitId'],
    businessUnitId,
  });

  // Count active filters
  const activeFilterCount = useActivePropertyFilterCount(queryFilters);
  const {
    categoriesList,
    fetchCategoriesApiStatus,
    loadCategories,
    updateCategoriesApiStatus,
    createCategoriesApiStatus,
    clearAPIStatusMessage,
  } = useCategories();
  const [showFilterModal, setShowFilterModal] = useState(false);

  const categoryFilterOptions = useMemo(
    () => getCategoryFilterOptions(categoriesList),
    [categoriesList],
  );

  const {
    searchText,
    setSearchText: setSearchTextInternal,
    query,
    setQuery,
    filteredItems,
    filterOptions,
    setFilterOptions,
  } = useGenericFilter<Category>(
    categoriesList,
    getItemValue,
    getSearchableFields,
    categoryFilterOptions,
    undefined, // nestedArrayConfig
    queryFilters.searchText || '', // Use persisted search text from queryFilters
  );

  // Temporary query state for filter modal (only applied on "Apply" click)
  const [tempQuery, setTempQuery] = useState(query);

  // Use custom hook for PropertyFilter persistence (search text + query tokens sync)
  const setSearchText = usePropertyFilterPersistence(
    setSearchTextInternal,
    setQueryFilters,
    queryFilters,
    setQuery,
  );

  // Local pagination for filtered results
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: tablePrefs.pageSize,
    });

  useEffect(() => {
    if (businessUnitId) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: businessUnitId,
        permissions: [permissionConfig.CATEGORY.EDIT_CATEGORY],
        divisions: getDivisions(),
      });
      loadBusinessUnits({
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
      loadData();
    }
    return () => {
      closeView();
      clearAPIStatusMessage();
    };
  }, [businessUnitId]);

  const selectCategory = (category: Category[]) => {
    setSelectedCategory(category);
  };
  const { checkUserPermissions, userPermission } = useUsers();
  const { businessUnitList, loadBusinessUnits } = useBusinessUnit();
  const isEditCategory = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionConfig.CATEGORY.EDIT_CATEGORY,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions]);

  const handleBuildCategories = () => {
    selectCategory([]);
    setIsEditing(true);
    openCategoryDetailsView('Create Category', null);
  };

  const loadData = () => {
    if (getUserBusinessUnitId()) {
      loadCategories({
        businessUnitId: getUserBusinessUnitId()!,
        maxResults: 1000, // Load all categories for client-side filtering
      });
    } else {
      alert('Please select a business unit');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    selectCategory([]);
    clearAPIStatusMessage();
    closeView();
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  function toggleCategoryStatus(id: string, status: boolean): void {
    throw new Error('Function not implemented.');
  }

  const openCategoryDetailsView = (title: string, category?: Category | null) => {
    clearAPIStatusMessage();
    setTitle(title);
    setContent(
      <QuestionCategoryDetailsForm
        category={category}
        isEditing={isEditing}
        onSave={() => {
          loadData();
          clearAPIStatusMessage();
          closeView();
        }}
        onCancel={handleCancel}
        onEdit={handleEdit}
      />,
    );
  };

  const handleSelectionChange = ({
    detail,
  }: {
    detail: { selectedItems: Category[] };
  }) => {
    if (detail.selectedItems.length > 0) {
      clearAPIStatusMessage();
      selectCategory(detail.selectedItems);
      setIsEditing(false);
      openCategoryDetailsView('Update Category', detail.selectedItems[0]);
    }
  };

  return (
    <ContentLayout>
      <ApiStatusFlashBar
        apiStatus={[updateCategoriesApiStatus, createCategoriesApiStatus]}
        clearMessages={() => {
          clearAPIStatusMessage();
        }}
      />
      <FilterModal
        visible={showFilterModal}
        onDismiss={() => {
          setTempQuery(query); // Reset temp query to current query
          setShowFilterModal(false);
        }}
        headerText="Filter categories"
        query={tempQuery}
        onChange={({ detail }: { detail: PropertyFilterProps.Query }) => {
          setTempQuery(detail as any);
        }}
        loading={false}
        filteringProperties={getFilterCategoriesProperties(
          getPersona() === Persona.PLATFORM_ADMIN,
        )}
        filteringOptions={filterOptions}
        filteringPlaceholder="Filter categories"
        countText={`${filteredItems?.length} matches`}
        onApply={() => {
          setCurrentPageIndex(1);
          setQuery(tempQuery); // Apply temp query to actual query
          setQueryFilters({ ...tempQuery, searchText: queryFilters.searchText });
          setShowFilterModal(false);
        }}
      />
      <Box margin={{ top: 'xxxl' }}>
        <Table
          columnDefinitions={getCategoryColumnDefinitions(
            toggleCategoryStatus,
            businessUnitList,
          ).filter((col) => visibleColumns.find((item) => item.id === col.id)?.visible)}
          items={paginatedItems}
          loading={fetchCategoriesApiStatus.isLoading}
          loadingText="Loading categories"
          selectionType="single"
          selectedItems={selectedCategory}
          onSelectionChange={handleSelectionChange}
          filter={
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchText}
                filteringPlaceholder="Search Category"
                filteringAriaLabel="Search Category"
                onChange={({ detail }) => {
                  setSearchText(detail.filteringText);
                }}
              />
              <Button
                data-testid="filter-button"
                iconName="filter"
                onClick={() => {
                  setTempQuery(query); // Initialize temp query with current query
                  setShowFilterModal(true);
                }}
              >
                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
              </Button>
            </SpaceBetween>
          }
          header={
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    data-testid="refresh-button"
                    iconName="refresh"
                    onClick={() => loadData()}
                  />
                  <ExportButton
                    data={filteredItems || []}
                    config={getExportCategoryConfig(businessUnitList)}
                    onError={(error) => {
                      alert(error);
                    }}
                  />
                  {isEditCategory() && (
                    <Button
                      data-testid="create-category-button"
                      variant="normal"
                      onClick={handleBuildCategories}
                    >
                      Create Category
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              Categories
            </Header>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          preferences={createCategoryPreferences(
            tablePrefs.pageSize,
            visibleColumns,
            (detail) => {
              if (
                detail.pageSize !== tablePrefs.pageSize &&
                detail.pageSize !== undefined
              ) {
                setTablePrefs((prev) => ({
                  ...prev,
                  pageSize: detail.pageSize || 10,
                }));
                setCurrentPageIndex(1);
              }
              if (detail.contentDisplay) {
                setVisibleColumns(detail.contentDisplay);
              }
            },
          )}
          visibleColumns={visibleColumns
            .filter((col) => col.visible)
            .map((col) => col.id)}
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Categories.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>
    </ContentLayout>
  );
};

export default QuestionsCategoryPage;
